<template>
  <div class="file-upload-field">
    <label v-if="label" class="file-upload-label">{{ label }}</label>
    
    <!-- Показываем текущий файл если есть -->
    <div v-if="currentFile && !selectedFile" class="current-file">
      <div class="file-info">
        <span class="file-name">{{ currentFile.name || currentFile.original_filename }}</span>
        <span class="file-size" v-if="currentFile.size">({{ formatFileSize(currentFile.size) }})</span>
        <div v-if="currentFilePreviewUrl" class="file-preview">
          <img
            v-if="showCurrentImagePreview"
            :src="currentFilePreviewUrl"
            alt="Current file preview"
            class="preview-image"
          />
          <video
            v-else-if="showCurrentVideoPreview"
            :src="currentFilePreviewUrl"
            class="preview-video"
            controls
            preload="metadata"
          />
        </div>
      </div>
      <div class="file-actions">
        <button type="button" @click="removeCurrentFile" class="remove-btn">
          <span>Удалить</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Показываем выбранный файл -->
    <div v-if="selectedFile" class="selected-file">
      <div class="file-info">
        <span class="file-name">{{ selectedFile.name }}</span>
        <span class="file-size">({{ formatFileSize(selectedFile.size) }})</span>
        <div v-if="selectedFilePreviewUrl" class="file-preview">
          <img
            v-if="showSelectedImagePreview"
            :src="selectedFilePreviewUrl"
            alt="Selected file preview"
            class="preview-image"
          />
          <video
            v-else-if="showSelectedVideoPreview"
            :src="selectedFilePreviewUrl"
            class="preview-video"
            controls
            preload="metadata"
          />
        </div>
      </div>
      <div class="file-actions">
        <button type="button" @click="cancelSelection" class="remove-btn icon-only" aria-label="Отменить выбор файла">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Область загрузки -->
    <div 
      v-if="!selectedFile || showUploadArea"
      class="upload-area"
      :class="{ 'drag-over': isDragOver, 'has-file': currentFile && !selectedFile, 'is-uploading': isUploading }"
      @click="triggerFileInput"
      @dragover.prevent="isDragOver = true"
      @dragleave="isDragOver = false"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        @change="handleFileSelect"
        class="file-input"
        style="display: none;"
      />
      
      <div class="upload-content">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="upload-icon">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
          <polyline points="7,10 12,15 17,10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        <p class="upload-text">
          {{ currentFile && !selectedFile ? 'Заменить файл' : 'Перетащите файл или нажмите для выбора' }}
        </p>
        <p class="upload-hint" v-if="acceptText">{{ acceptText }}</p>
      </div>
    </div>
    
    <!-- Прогресс загрузки -->
    <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
      </div>
      <span class="progress-text">{{ uploadProgress }}%</span>
    </div>
    
    <!-- Сообщение об ошибке -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { uploadFileWithProgress, uploadFileChunked } from '../services/api';
import { maybeCompressImage } from '../utils/compressToWebp';

export default {
  name: 'FileUploadField',
  props: {
    label: {
      type: String,
      default: ''
    },
    currentFile: {
      type: Object,
      default: null
    },
    accept: {
      type: String,
      default: '*/*'
    },
    acceptText: {
      type: String,
      default: ''
    },
    maxSize: {
      type: Number,
      default: 200 * 1024 * 1024 // 200MB по умолчанию
    },
    showUploadArea: {
      type: Boolean,
      default: true
    },
    useChunkedUpload: {
      type: Boolean,
      default: false // По умолчанию используем обычную загрузку
    },
    chunkSize: {
      type: Number,
      default: 1024 * 1024 // 1MB чанки
    },
    customUploadWithProgress: {
      type: Function,
      default: null
    },
    customUploadChunked: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFilePreviewUrl: '',
      isDragOver: false,
      uploadProgress: 0,
      errorMessage: '',
      isUploading: false,
      uploadAbortController: null
    };
  },
  computed: {
    acceptsImage() {
      return this.accept.split(',').some(type => type.trim().startsWith('image/'));
    },
    acceptsVideo() {
      return this.accept.split(',').some(type => type.trim().startsWith('video/'));
    },
    currentFilePreviewUrl() {
      return this.currentFile?.url || '';
    },
    showCurrentImagePreview() {
      return this.acceptsImage && Boolean(this.currentFilePreviewUrl);
    },
    showCurrentVideoPreview() {
      return this.acceptsVideo && Boolean(this.currentFilePreviewUrl);
    },
    showSelectedImagePreview() {
      return Boolean(this.selectedFilePreviewUrl) && (this.selectedFile?.type || '').startsWith('image/');
    },
    showSelectedVideoPreview() {
      return Boolean(this.selectedFilePreviewUrl) && (this.selectedFile?.type || '').startsWith('video/');
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },
    
    handleDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.validateAndSetFile(files[0]);
      }
    },
    
    validateAndSetFile(file) {
      this.errorMessage = '';
      
      // Проверка размера файла
      if (file.size > this.maxSize) {
        this.errorMessage = `Файл слишком большой. Максимальный размер: ${this.formatFileSize(this.maxSize)}`;
        return;
      }
      
      // Проверка типа файла
      if (this.accept !== '*/*') {
        const acceptedTypes = this.accept.split(',').map(type => type.trim());
        const fileType = file.type;
        const fileName = file.name.toLowerCase();
        
        const isValidType = acceptedTypes.some(acceptType => {
          if (acceptType.startsWith('.')) {
            return fileName.endsWith(acceptType.toLowerCase());
          }
          return fileType.match(acceptType.replace('*', '.*'));
        });
        
        if (!isValidType) {
          this.errorMessage = `Неподдерживаемый тип файла. Разрешенные типы: ${this.acceptText || this.accept}`;
          return;
        }
      }
      
      this.selectedFile = file;
      this.resetSelectedPreview();
      this.selectedFilePreviewUrl = URL.createObjectURL(file);
      this.uploadFile(file);
      this.$emit('file-selected', this.selectedFile);
    },
    
    async uploadFile(originalFile) {
      if (this.isUploading) return;

      try {
        this.isUploading = true;
        this.uploadProgress = 0;
        this.errorMessage = '';

        // Изображения пережимаем в WebP на клиенте перед загрузкой.
        // Видео и прочие файлы проходят без изменений.
        const file = await maybeCompressImage(originalFile);

        // Определяем метод загрузки
        const shouldUseChunked = this.useChunkedUpload || file.size > 2000 * 1024 * 1024; // Больше 2000MB
        
        // Выбираем функцию загрузки: кастомная или по умолчанию
        const uploadFn = shouldUseChunked 
          ? (this.customUploadChunked || uploadFileChunked)
          : (this.customUploadWithProgress || uploadFileWithProgress);
        
        let response;
        
        if (shouldUseChunked) {
          // Chunked загрузка для больших файлов
          response = await uploadFn(
            file,
            (progress) => {
              this.uploadProgress = progress;
            },
            (error) => {
              console.error('Ошибка chunked загрузки:', error);
            },
            this.chunkSize
          );
        } else {
          // Обычная загрузка с прогресс-баром
          response = await uploadFn(
            file,
            (progress) => {
              this.uploadProgress = progress;
            },
            (error) => {
              console.error('Ошибка загрузки:', error);
            }
          );
        }
        
        // Завершение загрузки
        this.uploadProgress = 100;
        this.$emit('file-uploaded', response);
        
        // Сброс прогресса через секунду
        setTimeout(() => {
          this.uploadProgress = 0;
          this.isUploading = false;
        }, 1000);
        
      } catch (error) {
        this.uploadProgress = 0;
        this.isUploading = false;
        this.errorMessage = 'Ошибка загрузки файла: ' + (error.message || 'Неизвестная ошибка');
        this.selectedFile = null;
        this.resetSelectedPreview();
        
        // Очистим input
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      }
    },
    
    cancelSelection() {
      this.selectedFile = null;
      this.resetSelectedPreview();
      this.uploadProgress = 0;
      this.errorMessage = '';
      this.isUploading = false;
      
      // Очистим input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      
      this.$emit('file-cancelled');
    },
    
    removeCurrentFile() {
      this.$emit('file-removed');
    },
    resetSelectedPreview() {
      if (this.selectedFilePreviewUrl) {
        URL.revokeObjectURL(this.selectedFilePreviewUrl);
        this.selectedFilePreviewUrl = '';
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  },
  beforeUnmount() {
    this.resetSelectedPreview();
  }
};
</script>

<style scoped>
.file-upload-field {
  margin: 12px 0;
}

.file-upload-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.current-file,
.selected-file {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
  background: hsl(var(--card));
  border: 1px solid hsl(var(--border));
  border-radius: 10px;
  margin-bottom: 8px;
  gap: 8px;
}

.file-info {
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
}

.file-name {
  font-weight: 500;
  color: hsl(var(--foreground));
  margin-bottom: 2px;
  word-break: break-word;
}

.file-size {
  font-size: 12px;
  color: hsl(var(--muted-foreground));
}

.file-actions {
  margin-left: 12px;
}

.remove-btn {
  background: hsl(var(--destructive) / 0.08);
  border: 1px solid hsl(var(--destructive) / 0.35);
  color: hsl(var(--destructive));
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background-color 0.2s, border-color 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
}

.remove-btn:hover {
  background: hsl(var(--destructive) / 0.16);
  border-color: hsl(var(--destructive) / 0.55);
}

.remove-btn.icon-only {
  width: 30px;
  height: 30px;
  padding: 0;
  justify-content: center;
}

.file-preview {
  margin-top: 10px;
}

.preview-image {
  max-width: 220px;
  max-height: 140px;
  border-radius: 6px;
  border: 1px solid hsl(var(--border));
  object-fit: cover;
  background: hsl(var(--muted));
}

.preview-video {
  max-width: 260px;
  max-height: 160px;
  border-radius: 6px;
  border: 1px solid hsl(var(--border));
  background: #000;
}

.upload-area {
  border: 1px dashed hsl(var(--border));
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: hsl(var(--card));
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: hsl(var(--accent) / 0.8);
  background: hsl(var(--accent) / 0.1);
}

.upload-area.is-uploading {
  pointer-events: none;
  opacity: 0.75;
}

.upload-area.has-file {
  border-style: solid;
  border-color: hsl(var(--accent) / 0.45);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon {
  color: hsl(var(--muted-foreground));
  margin-bottom: 12px;
}

.upload-text {
  color: hsl(var(--foreground));
  margin: 0 0 4px 0;
  font-weight: 500;
}

.upload-hint {
  color: hsl(var(--muted-foreground));
  font-size: 12px;
  margin: 0;
}

.upload-progress {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: hsl(var(--muted));
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: hsl(var(--accent));
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: hsl(var(--muted-foreground));
  min-width: 35px;
  text-align: right;
}

.error-message {
  margin-top: 8px;
  padding: 10px;
  background: hsl(var(--destructive) / 0.1);
  border: 1px solid hsl(var(--destructive) / 0.35);
  border-radius: 8px;
  color: hsl(var(--destructive));
  font-size: 14px;
}
</style> 
